import React, { useCallback, useContext, useState } from "react";
import { Field, Form, Formik } from "formik";
import styled from "styled-components";
import TitleFormGroup from "search-filter/components/form-elements/TitleFormGroup";
import DescriptionFormGroup from "search-filter/components/form-elements/DescriptionFormGroup";
import { Button, Input } from "components/bootstrap";
import { HoverForHelp, Icon, ModalSubmit } from "components/common";
import FormWarningsContext from "contexts/FormWarningsContext";
import FormWarningsProvider from "contexts/FormWarningsProvider";
import validateSearchFilterForm from "search-filter/validateForm";
import QueryStringFormGroup from "search-filter/components/form-elements/QueryStringFromGroup";
import useUserDateTime from "hooks/useUserDateTime";
import usePluginEntities from "hooks/usePluginEntities";
import { pluggableValidationPayload } from "views/logic/searchbar/pluggableSearchBarControlsHandler";
import useSearchFormValues from "search-filter/hooks/useSearchFormValues";
import ValidateOnParameterChange from "views/components/searchbar/ValidateOnParameterChange";
import useHandlerContext from "search-filter/hooks/useHandlerContext";
import useParameters from "search-filter/hooks/useParameters";
const StyledSaveFilter = styled.div`
  display: flex;
  margin-right: 10px;
  align-items: center;
`;
const StyledFlexDiv = styled.div`
  display: flex;
  justify-content: space-between;
`;
const StyledHoverForHelp = styled(({ ...props }) => /* @__PURE__ */ React.createElement(HoverForHelp, { ...props }))`
  margin-left: 10px;
`;
const ToggleButton = styled(Button)`
  padding: 0;
  margin-bottom: 10px;
`;
const ToggleIcon = styled(Icon)`
  margin-left: 5px;
`;
const initialFormValues = { queryString: "", title: "", description: "", checked: "" };
const SearchFilterForm = ({
  checkboxLabel,
  helpText,
  helpTitle,
  formDescription = void 0,
  initialValues = initialFormValues,
  onCancel,
  onSubmit,
  showCheckbox = true,
  submitButtonText,
  submitLoadingText
}) => {
  const { setFieldWarning } = useContext(FormWarningsContext);
  const { userTimezone } = useUserDateTime();
  const pluggableSearchBarControls = usePluginEntities("views.components.searchBar");
  const { parameterBindings, timerange, streams } = useSearchFormValues();
  const [showTitleAndDescription, setShowTitleAndDescription] = useState(false);
  const _onSubmit = useCallback(
    ({ checked, ...formValues }) => onSubmit(formValues, checked),
    [onSubmit]
  );
  const handlerContext = useHandlerContext();
  const _validate = useCallback(
    (values) => validateSearchFilterForm(values, {
      setFieldWarning,
      pluggableValidationPayload: pluggableValidationPayload(
        { ...values, parameterBindings },
        handlerContext,
        pluggableSearchBarControls
      ),
      userTimezone
    }),
    [setFieldWarning, parameterBindings, handlerContext, pluggableSearchBarControls, userTimezone]
  );
  const parametersContext = useParameters();
  const parameters = parametersContext == null ? void 0 : parametersContext.parameters;
  return /* @__PURE__ */ React.createElement(Formik, { onSubmit: _onSubmit, validateOnMount: true, validate: _validate, initialValues }, ({ isValid, values, isSubmitting }) => {
    const disableSubmit = !isValid || !values.queryString;
    return /* @__PURE__ */ React.createElement(Form, { className: "form" }, /* @__PURE__ */ React.createElement(QueryStringFormGroup, { timeRange: timerange, streams, labelClassName: "", wrapperClassName: "" }), /* @__PURE__ */ React.createElement(
      ToggleButton,
      {
        bsStyle: "link",
        onClick: () => {
          setShowTitleAndDescription((cur) => !cur);
        }
      },
      "Configure title and description",
      /* @__PURE__ */ React.createElement(ToggleIcon, { name: showTitleAndDescription ? "keyboard_arrow_up" : "keyboard_arrow_down" })
    ), showTitleAndDescription && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(TitleFormGroup, { labelClassName: "", wrapperClassName: "" }), /* @__PURE__ */ React.createElement(DescriptionFormGroup, { labelClassName: "", wrapperClassName: "" })), formDescription && formDescription, /* @__PURE__ */ React.createElement(StyledFlexDiv, null, /* @__PURE__ */ React.createElement("div", null, showCheckbox && /* @__PURE__ */ React.createElement(StyledSaveFilter, null, /* @__PURE__ */ React.createElement(Field, { name: "checked" }, ({ field: { name, value, onChange }, meta }) => /* @__PURE__ */ React.createElement(
      Input,
      {
        id: name,
        error: meta == null ? void 0 : meta.error,
        label: checkboxLabel,
        type: "checkbox",
        onChange,
        formGroupClassName: "",
        value: value ? "checked" : ""
      }
    )), /* @__PURE__ */ React.createElement(StyledHoverForHelp, { title: helpTitle }, helpText))), /* @__PURE__ */ React.createElement(
      ModalSubmit,
      {
        onCancel,
        bsSize: "small",
        disabledSubmit: disableSubmit,
        submitButtonText,
        submitLoadingText,
        isAsyncSubmit: true,
        isSubmitting
      }
    )), /* @__PURE__ */ React.createElement(ValidateOnParameterChange, { parameters }));
  });
};
const SearchFilterFormWrapper = ({ ...props }) => /* @__PURE__ */ React.createElement(FormWarningsProvider, null, /* @__PURE__ */ React.createElement(SearchFilterForm, { ...props }));
export default SearchFilterFormWrapper;
